import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed } from '@angular/core';
import { Port } from '@navstation-portal/ports/data-model/ports';
import { portCodeToSelectOption } from '@navstation-portal/ports/util/ports';
import {
  AnyOffshoreSectionState,
  OffshoreEventType,
  OffshoreFormConfig,
  OffshoreOperationalState,
  getSelectOptionFromEventCode,
  getSelectOptionFromOperationCode,
} from '@navstation-portal/reporting/data-model/offshore-report';
import { GroupedFormData } from '@navstation-portal/reporting/data-model/ui';
import { INIT_MODES, InitMode } from '@navstation-portal/reporting/data-model/vessel-report';
import { LatLngModel, SelectOption } from '@navstation-portal/shared/data-model/common';
import { resolveTimezoneSelectOption } from '@navstation-portal/shared/util/time';
import { signalStore, withComputed, withState } from '@ngrx/signals';
import { initStateField } from '../../../offshore-report-utils/store.utility';
import { createDynamicSectionState } from '../../offshore-vm-builder-helpers';
import { withOffshoreFormStoreReducer } from '../../with-offshore-form-store-reducer';
import { buildOffshoreOperationalVm } from './offshore-operational-vm-builders';
import { withOffshoreOperationalEffects } from './with-offshore-operational-effects';
import { offshoreOperationalEvents } from './with-offshore-operational-events';

const createEmptyLatLng = () => ({
  latDegrees: undefined,
  latMinutes: undefined,
  latSeconds: undefined,
  latDirection: undefined,
  longDegrees: undefined,
  longMinutes: undefined,
  longSeconds: undefined,
  longDirection: undefined,
});

const isSectionEnabled = (key: string, formConfig?: OffshoreFormConfig, eventType?: OffshoreEventType): boolean => {
  if (!formConfig) return true;
  const section = formConfig?.tabs['operational']?.sections?.[key];
  if (!section) return false;
  if (!section.visible) return false;
  if (!section.visible_types?.length) return true;
  return !eventType || section.visible_types.includes(eventType);
};

function buildOperationalState(
  data?: OffshoreOperationalState,
  formConfig?: OffshoreFormConfig,
  isImport = true // are we building state from an existing report (import) or carrying over from previous report
) {
  const eventType = data?.reportOptions?.eventType;
  const eventOption = getSelectOptionFromEventCode(eventType);
  const enabled = (key: string) => isSectionEnabled(key, formConfig, eventType);

  const operationOption = getSelectOptionFromOperationCode(data?.reportOptions?.operationType);

  return {
    reportOptions: {
      eventType: initStateField<SelectOption>(eventOption),
      operationType: initStateField<SelectOption>(operationOption),
    },
    ...(enabled('reportDetails') && {
      reportDetails: {
        reportNumber: initStateField<number>(data?.reportDetails?.reportNumber ? data?.reportDetails?.reportNumber : 1),
        reportCreation: initStateField<Date>(new Date()),
        reportFrom: initStateField<Date>(
          data?.reportDetails?.reportFrom ? new Date(data?.reportDetails?.reportFrom) : undefined
        ),
        reportTo: initStateField<Date>(
          data?.reportDetails?.reportTo ? new Date(data?.reportDetails?.reportTo) : undefined
        ),
        reportPeriod: initStateField<number>(data?.reportDetails?.reportPeriod),
        reportSubmitted: initStateField<Date | undefined>(data?.reportDetails?.reportSubmitted),
        timeSailed: initStateField<number>(data?.reportDetails?.timeSailed),
        timezone: initStateField<SelectOption | undefined>(
          data?.reportDetails?.timezone ? data.reportDetails.timezone : undefined
        ),
        previousTimezone: initStateField<SelectOption>(
          data?.reportDetails?.previousTimezone ? data.reportDetails.previousTimezone : undefined
        ),
      },
    }),
    ...(enabled('voyageDetails') && {
      voyageDetails: {
        voyageNumber: initStateField(data?.voyageDetails?.voyageNumber),
        voyageFrom: initStateField<SelectOption>(data?.voyageDetails?.voyageFrom),
        voyageTo: initStateField<SelectOption>(data?.voyageDetails?.voyageTo),
        eta: initStateField<Date>(data?.voyageDetails?.eta ? new Date(data.voyageDetails.eta) : undefined),
        etaTimezone: initStateField<SelectOption>(data?.voyageDetails?.etaTimezone),
      },
    }),
    ...(enabled('crewAndPassengers') && {
      crewAndPassengers: {
        numCrew: initStateField<number>(data?.crewAndPassengers?.numCrew),
        numMarineCrew: initStateField<number>(data?.crewAndPassengers?.numMarineCrew),
        numContractors: initStateField<number>(data?.crewAndPassengers?.numContractors),
        numClients: initStateField<number>(data?.crewAndPassengers?.numClients),
        numPassengers: initStateField<number>(data?.crewAndPassengers?.numPassengers),
      },
    }),
    ...(enabled('miscellaneous') && {
      miscellaneous: {
        captainName: initStateField<string>(data?.miscellaneous?.captainName),
        chiefEngineer: initStateField<string>(data?.miscellaneous?.chiefEngineer),
        cargoWeight: initStateField<number>(data?.miscellaneous?.cargoWeight),
        clientChartererName: initStateField<string>(data?.miscellaneous?.clientChartererName),
        supportVessels: initStateField<string>(data?.miscellaneous?.supportVessels),
        activityLog: initStateField<string>(data?.miscellaneous?.activityLog),
      },
    }),
    ...(enabled('operatingConditions') && {
      operatingConditions: {
        cargoLocation: initStateField<SelectOption>(),
        tugsAllFast: initStateField<number>(),
        tugsLastLine: initStateField<number>(),
        draughtForward: initStateField<number>(),
        draughtAft: initStateField<number>(),
        trim: initStateField<number>(),
        ballastWaterOnBoard: initStateField<number>(),
        ballastWaterIntake: initStateField<number>(),
        ballastWaterDischarged: initStateField<number>(),
        ballastWaterSampleDate: initStateField<Date>(),
        ballastWaterCountOfCells: initStateField<number>(),
      },
    }),
    ...(enabled('portAndPosition') && {
      portAndPosition: {
        portName: initStateField<string>(data?.portAndPosition?.portName),
        portPosition: initStateField<LatLngModel>(
          data?.portAndPosition?.portPosition
            ? {
                latDegrees: data.portAndPosition.portPosition.latDegrees,
                latMinutes: data.portAndPosition.portPosition.latMinutes,
                latSeconds: data.portAndPosition.portPosition.latSeconds,
                latDirection: data.portAndPosition.portPosition.latDirection,
                longDegrees: data.portAndPosition.portPosition.longDegrees,
                longMinutes: data.portAndPosition.portPosition.longMinutes,
                longSeconds: data.portAndPosition.portPosition.longSeconds,
                longDirection: data.portAndPosition.portPosition.longDirection,
              }
            : createEmptyLatLng()
        ),
      },
    }),
    ...(enabled('stabilityConditions') && {
      stabilityConditions: {
        gmReserve: initStateField<number>(),
        deadWeightReserve: initStateField<number>(),
      },
    }),
    ...(enabled('portOperationsTimestamp') && {
      portOperationsTimestamp: {
        etd: initStateField<Date>(data?.portOperationsTimestamp?.etd),
        norTendered: initStateField<Date>(),
        allFast: initStateField<Date>(),
        hosesConnected: initStateField<Date>(),
        commenceLoad: initStateField<Date>(),
        finishLoad: initStateField<Date>(),
        hosesDisconnected: initStateField<Date>(),
        lastLine: initStateField<Date>(),
        cargoDocsOnboard: initStateField<Date>(),
        startDischarging: initStateField<Date>(),
        endDischarging: initStateField<Date>(),
        completedTankInspection: initStateField<Date>(),
      },
    }),
    ...(enabled('nextPort') && {
      nextPort: {
        awaitingOrders: initStateField<boolean>(),
        nextPort: initStateField<string>(data?.nextPort?.nextPort),
        remainingDistance: initStateField<number>(),
        remainingTime: initStateField<number>(),
        nextTimezone: initStateField<SelectOption>(resolveTimezoneSelectOption(data?.nextPort?.nextTimezone)),
        etaPilotStation: initStateField<Date>(),
      },
    }),
    dynamicSection: createDynamicSectionState(formConfig?.tabs['operational']?.customFields),
  };
}

const initBlank = () => buildOperationalState();

const initWithCarryOver = (data: OffshoreOperationalState, formConfig?: OffshoreFormConfig) => {
  return buildOperationalState(data, formConfig, false);
};

function initWithExistingReport(data: OffshoreOperationalState, formConfig?: OffshoreFormConfig) {
  return buildOperationalState(data, formConfig);
}

const initOffshoreOperationalStateReducer = (
  initialData: AnyOffshoreSectionState,
  initMode: InitMode,
  _formFieldRules?: Record<string, GroupedFormData>,
  formConfig?: OffshoreFormConfig
) => {
  const data = initialData as OffshoreOperationalState;
  switch (initMode) {
    case INIT_MODES.CARRY_OVER:
      return initWithCarryOver(data, formConfig);
    case INIT_MODES.IMPORT:
      return initWithExistingReport(data, formConfig);
    case INIT_MODES.BLANK:
      return initBlank();
    default:
      return initBlank();
  }
};

export const OffshoreOperationalFormStore = signalStore(
  { providedIn: 'root' },
  withDevtools('OffshoreOperationalFormStore'),
  withState(initBlank()),
  withOffshoreOperationalEffects(),
  withOffshoreFormStoreReducer(offshoreOperationalEvents, initOffshoreOperationalStateReducer, initBlank),
  withComputed((store) => ({
    vm: computed(() => {
      return buildOffshoreOperationalVm(store);
    }),
  }))
);
